/**
 * «عجلة الأبعاد» — `GET /api/v3/org/wheel` (integration guide §3).
 *
 * A SELF-ASSESSMENT: the member answers short questions and their answers ARE
 * the scores. Nothing is inferred from behaviour, so there is no derivation to
 * write here — only the wire shape, already worded for this organization.
 *
 * Two rules shape every type below:
 *  · `core` is SEPARATE from `dimensions` (trap 5) — two render slots, never one
 *    array. The core is the dimension the crisis bridge reads and an org can
 *    never switch it off.
 *  · `name` / `question` / `meaning` / `band_label` / `action_label` arrive
 *    already worded for this org's kind and its own overrides (§3, §1.5). They
 *    are contractual copy from signed decks — rendered as sent, NEVER re-mapped
 *    from the key in the client.
 */

/** Catalogue keys. Open-ended on purpose — an org can show fewer, never more. */
export type OrgWheelDimensionKey = 'core' | 'bod' | 'pro' | 'soc' | 'spi' | 'int' | 'fin' | 'env' | 'emo';

/** `balanced` ≥75 · `acceptable` ≥50 · `needs_care` <50. Null when never answered. */
export type OrgWheelBand = 'balanced' | 'acceptable' | 'needs_care';

/** Where a dimension's CTA goes. Route on THIS, never on the label (§3.1). */
export type OrgWheelAction = 'khoyek' | 'programs' | 'article' | 'seminar' | 'care';

/** What `action_target` points at; all-null for `khoyek` / `care`. */
export type OrgWheelTargetType = 'programme' | 'article' | 'seminar';

export interface OrgWheelActionTarget {
  type: OrgWheelTargetType | null;
  id: number | null;
  title: string | null;
  /**
   * False when the target was unpublished. The server has ALREADY demoted a
   * fully-dead row to `khoyek` before we see it (§3.1 "the dead-end fallback"),
   * so `action: 'programs'` + `exists: false` still means the slug route lives —
   * fall back to {@link OrgWheelDimension.programmeKey}, do not hide the CTA.
   */
  exists: boolean;
}

export interface OrgWheelDimension {
  key: string;
  isCore: boolean;
  /** Already worded for this org — render as sent. */
  name: string;
  question: string;
  meaning: string;
  /** Design tokens, org-independent today. */
  icon: string;
  hue: string;
  /** The LATEST answer whenever it was given, not only today. Null = never answered. */
  score: number | null;
  band: OrgWheelBand | null;
  bandLabel: string | null;
  action: OrgWheelAction;
  /** The button text. ALWAYS moves with {@link action} — never render it next to another destination. */
  actionLabel: string;
  /** Legacy slug route (`GET /v3/programs?key=<slug>`). A hint only; prefer {@link actionTarget}. */
  programmeKey: string | null;
  actionTarget: OrgWheelActionTarget;
}

/** One option of the answer scale. Rendered FROM the payload, never hardcoded (§3.1). */
export interface OrgWheelScaleOption {
  value: number;
  label: string;
}

export interface OrgWheel {
  /** Null only when the org has no core configured — render at the wheel's centre. */
  core: OrgWheelDimension | null;
  /** The other dimensions, ALREADY in the order this org wants. Do not re-sort. */
  dimensions: readonly OrgWheelDimension[];
  /** Today's average if checked in, else the average of the latest score per dimension. */
  composite: number | null;
  /** 0 when not checked in today. */
  answeredToday: number;
  /** How many dimensions THIS org shows — not always 9 (trap 6). */
  total: number;
  checkedInToday: boolean;
  /** Consecutive days with a check-in. */
  streak: number;
  /** Core score ≤ 25 → surface the urgent-session CTA. A floor check, not a diagnosis. */
  crisis: boolean;
  scale: readonly OrgWheelScaleOption[];
}

/** The empty wheel a pre-hydration render paints against. */
export const EMPTY_ORG_WHEEL: OrgWheel = {
  core: null,
  dimensions: [],
  composite: null,
  answeredToday: 0,
  total: 0,
  checkedInToday: false,
  streak: 0,
  crisis: false,
  scale: [],
};

/** `{ core: 50, bod: 75 }` — a PARTIAL map is normal and expected (§3.2). */
export type OrgWheelAnswers = Readonly<Record<string, number>>;

/** The only four values the server accepts; anything else is a Laravel 422. */
export const ORG_WHEEL_ALLOWED_ANSWERS: readonly number[] = [25, 50, 75, 100];

/** Client-side guard so an out-of-range answer never leaves the browser. */
export function isValidWheelAnswer(value: number): boolean {
  return ORG_WHEEL_ALLOWED_ANSWERS.includes(value);
}

/** Core first, then the org's own order — the sequence the daily check-in walks. */
export function wheelCheckinOrder(wheel: OrgWheel): readonly OrgWheelDimension[] {
  return wheel.core ? [wheel.core, ...wheel.dimensions] : wheel.dimensions;
}

/**
 * Drop every answer whose key is not in the wheel we just rendered.
 *
 * Trap 7: the server filters unknown keys SILENTLY and answers 200 with the
 * unchanged wheel, so a key we invent would look like it landed. Filtering here
 * means the only keys we can possibly send are ones the org actually shows.
 */
export function retainKnownAnswers(wheel: OrgWheel, answers: OrgWheelAnswers): OrgWheelAnswers {
  const known = new Set(wheelCheckinOrder(wheel).map((d) => d.key));
  const out: Record<string, number> = {};
  for (const [key, value] of Object.entries(answers)) {
    if (known.has(key) && isValidWheelAnswer(value)) out[key] = value;
  }
  return out;
}

/**
 * Did a check-in actually land? A 200 alone does not prove it (trap 7) — so the
 * facade compares `answered_today` against what it sent and warns when the server
 * kept fewer answers than we posted.
 */
export function checkinLanded(before: OrgWheel, after: OrgWheel, sentCount: number): boolean {
  if (sentCount === 0) return false;
  if (after.answeredToday > before.answeredToday) return true;
  // An AMENDMENT re-answers a dimension already counted today, so the count can
  // legitimately stay put — `checked_in_today` flipping on, or any score moving,
  // is then the proof.
  if (!before.checkedInToday && after.checkedInToday) return true;
  return scoreFingerprint(before) !== scoreFingerprint(after);
}

function scoreFingerprint(wheel: OrgWheel): string {
  return wheelCheckinOrder(wheel)
    .map((d) => `${d.key}:${d.score ?? '-'}`)
    .join('|');
}

/** `answered_today / total` as a 0–100 percentage; 0 when the org shows nothing. */
export function wheelProgressPercent(wheel: OrgWheel): number {
  if (wheel.total <= 0) return 0;
  return Math.min(100, Math.round((wheel.answeredToday / wheel.total) * 100));
}
